import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { AsciiField } from '@/components/generals/ascii-field'
import { NativeLogo } from '@/components/generals/native-logo'

type Gsap = typeof import('gsap').gsap

interface LogoParts {
    square: Element
    cuts: Element[]
    letters: Element[]
}

const LETTER_OFFSET = -358.84
const CELL_RATIO = 0.05
const JITTER = 0.5
const DISSOLVE_MS = 800
const LOGO_DELAY_MS = 300
const HOLD_MS = 450
const DEADLINE_MS = 2000
const FAILSAFE_MS = 4000

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, Math.max(0, ms)))

const easeOutQuad = (t: number): number => 1 - (1 - t) * (1 - t)

const markPreloaded = (): void => {
    document.documentElement.setAttribute('data-preloaded', '')
    document.dispatchEvent(new CustomEvent('native:preloaded'))
}

const hide = (element: HTMLElement): void => {
    element.hidden = true
    element.style.display = 'none'
}

const collectLogo = (logo: Element): LogoParts | null => {
    const square = logo.querySelector('[data-logo-square]')
    const bar = logo.querySelector('[data-logo-bar]')
    const diag = logo.querySelector('[data-logo-diag]')
    const tri = logo.querySelector('[data-logo-tri]')
    const letters = Array.from(logo.querySelectorAll('[data-logo-letter]'))

    if (!square || !bar || !diag || !tri || letters.length !== 6) return null

    return { square, cuts: [bar, diag, tri], letters }
}

const setLogo = (gsap: Gsap, parts: LogoParts): void => {
    const [bar, diag, tri] = parts.cuts

    gsap.set(parts.square, { scaleY: 0, svgOrigin: '29.37 54.55' })
    gsap.set(bar, { scaleY: 0, svgOrigin: '13.24 7.87' })
    gsap.set(diag, { scale: 0, svgOrigin: '11.84 7.87' })
    gsap.set(tri, { scale: 0, svgOrigin: '50.88 7.87' })
    gsap.set(parts.letters, { x: LETTER_OFFSET })
}

const playLogo = (gsap: Gsap, parts: LogoParts) => {
    return gsap.timeline()
        .to(parts.square, { scaleY: 1, duration: 0.55, ease: 'expo.out' }, 0)
        .to(parts.cuts, { scaleX: 1, scaleY: 1, duration: 0.5, ease: 'expo.out', stagger: 0.08 }, 0.2)
        .to(parts.letters, { x: 0, duration: 0.9, ease: 'expo.out', stagger: { each: 0.04, from: 'end' } }, 0.5)
}

// Pixel dissolve: square cells clip away row by row with a random jitter, then the loader is removed.
const dissolve = (element: HTMLElement): void => {
    const { width, height } = element.getBoundingClientRect()
    const size = Math.max(5, Math.floor(width * CELL_RATIO))
    const cols = Math.ceil(width / size)
    const rows = Math.ceil(height / size)
    const jitter = rows * JITTER
    const offsets = new Float32Array(rows * cols)

    for (let i = 0; i < offsets.length; i++) offsets[i] = Math.random() * jitter

    const from = rows + jitter
    const span = rows + 2 * jitter
    const started = performance.now()

    element.style.pointerEvents = 'none'

    const step = (now: number): void => {
        const progress = Math.min(1, (now - started) / DISSOLVE_MS)
        const edge = from - span * easeOutQuad(progress)

        let path = ''

        for (let row = 0; row < rows && row <= edge + jitter + 1; row++) {
            const y = row * size

            for (let col = 0; col < cols; col++) {
                if (row < edge + offsets[row * cols + col]) {
                    const x = col * size

                    path += `M${x} ${y}h${size + 1}v${size + 1}h${-(size + 1)}Z`
                }
            }
        }

        if (progress < 1 && path) {
            element.style.clipPath = `path('${path}')`
            requestAnimationFrame(step)
        }
        else {
            element.style.clipPath = ''
            hide(element)
            markPreloaded()
        }
    }

    requestAnimationFrame(step)
}

export const Preloader: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const element = ref.current

        if (!element || element.hidden || getComputedStyle(element).display === 'none' || performance.now() >= FAILSAFE_MS) {
            if (element) hide(element)
            markPreloaded()
            return
        }

        element.style.animation = 'none'

        const raceDeadline = <T,>(promise: Promise<T>): Promise<T | null> => {
            return Promise.race([promise, wait(DEADLINE_MS - performance.now()).then(() => null)])
        }

        const run = async (): Promise<void> => {
            const logo = element.querySelector<SVGSVGElement>('[data-logo]')
            const parts = logo ? collectLogo(logo) : null
            const gsap = await raceDeadline(import('gsap').then((mod) => mod.gsap)).catch(() => null)

            if (gsap && parts && logo) {
                setLogo(gsap, parts)
                logo.style.opacity = '1'
                await wait(LOGO_DELAY_MS - performance.now())
                await playLogo(gsap, parts).then()
            }
            else if (logo) {
                logo.style.opacity = '1'
            }

            await raceDeadline(document.fonts?.ready ?? Promise.resolve())
            await wait(HOLD_MS)
            dissolve(element)
        }

        run()
    }, [])

    return (
        <div ref={ref} className='preloader' data-preloader='' aria-hidden='true'>
            <AsciiField className='preloader-ascii' seed={11} mask='frame' cell={10} power={1.3} gain={1.9} />
            <NativeLogo className='preloader-logo' idPrefix='preloader' />
        </div>
    )
}
